import { useTranslation } from 'react-i18next';
import { Briefcase, Calendar, Clock, User, Sparkles } from 'lucide-react';
import { SummaryCard } from '../components';
import type { WizardStep } from '../hooks/useAvailabilityWizard';

const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;

interface FirstTimeReviewStepProps {
  serviceName: string;
  serviceDuration: number;
  servicePrice: string;
  currency: string;
  selectedDays: number[];
  startTime: string;
  endTime: string;
  providerName?: string;
  onEditStep: (step: WizardStep) => void;
}

export function FirstTimeReviewStep({
  serviceName,
  serviceDuration,
  servicePrice,
  currency,
  selectedDays,
  startTime,
  endTime,
  providerName,
  onEditStep,
}: FirstTimeReviewStepProps) {
  const { t } = useTranslation('staff');

  const currencySymbol = currency === 'EUR' ? '€' : currency === 'USD' ? '$' : currency;

  const formatDuration = (mins: number) => {
    if (mins >= 60) {
      const hours = Math.floor(mins / 60);
      const remainingMins = mins % 60;
      if (remainingMins > 0) {
        return `${hours}h ${remainingMins}min`;
      }
      return `${hours} ${t('common.hour', { count: hours })}`;
    }
    return `${mins} min`;
  };

  const formatPrice = () => {
    if (!servicePrice || servicePrice === '0') {
      return t('services.free', 'Free');
    }
    return `${currencySymbol}${parseFloat(servicePrice).toFixed(2)}`;
  };

  const formatDays = () => {
    if (selectedDays.length === 7) {
      return t('availability.wizard.presets.everyday', 'Every day');
    }
    if (selectedDays.length === 5 && [1, 2, 3, 4, 5].every((d) => selectedDays.includes(d))) {
      return t('availability.wizard.presets.weekdays', 'Weekdays');
    }
    if (selectedDays.length === 2 && [0, 6].every((d) => selectedDays.includes(d))) {
      return t('availability.wizard.presets.weekend', 'Weekend');
    }
    return selectedDays
      .sort((a, b) => a - b)
      .map((d) => t(`availability.days.${DAY_KEYS[d]}`))
      .join(', ');
  };

  return (
    <div className="p-6 space-y-4">
      {/* Success header */}
      <div className="text-center py-4">
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center mx-auto mb-3">
          <Sparkles className="w-8 h-8 text-white" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">
          {t('availability.wizard.firstTime.ready', "You're almost ready!")}
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          {t('availability.wizard.firstTime.reviewDetails', 'Review your setup before launching')}
        </p>
      </div>

      {/* Review cards */}
      <div className="space-y-3">
        {/* Service card */}
        <SummaryCard
          icon={Briefcase}
          iconColor="text-blue-600"
          iconBgColor="bg-blue-100"
          label={t('availability.wizard.review.service', 'Service')}
          value={
            <div>
              <p className="font-medium">{serviceName}</p>
              <p className="text-sm text-gray-500">
                {formatDuration(serviceDuration)} · {formatPrice()}
              </p>
            </div>
          }
          onEdit={() => onEditStep(1)}
        />

        {/* Provider card (if applicable) */}
        {providerName && (
          <SummaryCard
            icon={User}
            iconColor="text-purple-600"
            iconBgColor="bg-purple-100"
            label={t('availability.wizard.review.provider', 'Team Member')}
            value={providerName}
            onEdit={() => onEditStep(2)}
          />
        )}

        {/* Schedule card */}
        <SummaryCard
          icon={Calendar}
          iconColor="text-amber-600"
          iconBgColor="bg-amber-100"
          label={t('availability.wizard.review.schedule', 'Schedule')}
          value={formatDays()}
          onEdit={() => onEditStep(3)}
        />

        {/* Time card */}
        <SummaryCard
          icon={Clock}
          iconColor="text-emerald-600"
          iconBgColor="bg-emerald-100"
          label={t('availability.wizard.review.timeRange', 'Time Range')}
          value={`${startTime} - ${endTime}`}
          onEdit={() => onEditStep(3)}
        />
      </div>

      {/* Launch info */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
        <p className="text-sm text-green-800">
          {t(
            'availability.wizard.firstTime.launchNote',
            "Tap 'Create' to launch your booking page. Customers will be able to book this service during your available hours."
          )}
        </p>
      </div>
    </div>
  );
}
